import React from 'react'
import { Button, Field, Input, Select } from '@/components/ds'
import { Ic } from '@/components/icons'
import {
  IncompleteCampaignError,
  activateCampaign,
  bindCampaignTemplate,
  createCampaign,
  discardDraftIfEmpty,
  getCampaign,
  getCampaignReadiness,
  getLeadFieldGaps,
  listIdentities,
  listLeads,
  listTemplates,
  updateCampaign,
  type CampaignDraft,
} from '@/lib/db'
import { describeGaps, requiredLeadFields, type LeadFieldGap } from '@shared/gaps'
import { processDueSendsNow } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { useToast } from '@/lib/toast'
import { isValidEmail } from '@/lib/format'
import { MIN_DELAY_MINUTES, validateDelay } from '@shared/schedule'
import { resolveSenderVars, unresolvedTokens } from '@shared/render'
import { LEADS_PAGE_SIZE } from '@/components/leads'
import { usePageSize } from '@/components/common'
import { DelayControl } from '@/components/DelayControl'
import { LeadsStep, ReviewStep, TemplateStep } from './CampaignWizardSteps'
import type { Campaign, Lead, Template } from '@shared/types'
import { MISSING_LABEL, type MissingPiece } from '@shared/readiness'

// Template BEFORE Leads, deliberately: the template decides which merge
// columns every lead must carry ({{role}} needs a role), so choosing it first
// lets the Add-lead form, the CSV importer's expected-columns guidance and the
// reconciliation gate all know what a lead has to look like.
const STEPS = [
  { key: 'details', label: 'Details', hint: 'Name, sender, pacing' },
  { key: 'template', label: 'Template', hint: 'What they receive' },
  { key: 'leads', label: 'Leads', hint: 'Who receives it' },
  { key: 'review', label: 'Review & send', hint: 'Check, then launch' },
] as const

type StepKey = (typeof STEPS)[number]['key']

const BLANK: CampaignDraft = {
  name: '',
  smtp_account_id: null,
  // Delay OFF by default: a new campaign sends as soon as it is launched. The
  // old default (random, up to an hour) meant "Send now" queued everything up
  // to 60 minutes out and then showed "Scheduled", which read as a broken send.
  delay_enabled: false,
  delay_mode: 'random_1h',
  delay_hours: 1,
  delay_minutes: MIN_DELAY_MINUTES,
  random_window_minutes: 60,
  daily_cap: 500,
  throttle_seconds: 60,
  status: 'draft',
  from_name: null,
  reply_to: null,
  company: null,
  sender_title: null,
  starts_at: null,
  window_enabled: false,
  window_start_min: 540,
  window_end_min: 1020,
  send_tz: null,
}

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

/** Which step owns each missing piece. The wording lives in @shared/readiness. */
const MISSING_STEP: Record<MissingPiece, StepKey> = {
  campaign: 'details',
  identity: 'details',
  leads: 'leads',
  template: 'template',
}

/**
 * The continuous campaign flow. Everything here is a re-sequencing of pieces
 * that already exist (campaign fields, CSV import, template editor/picker) —
 * the wizard's own job is the draft lifecycle and the ordering.
 *
 * A draft campaign never sends and never shows as active; finishing is the only
 * thing that flips it out of 'draft'.
 */
export function CampaignWizard({
  resumeId,
  narrow,
  onClose,
  onFinished,
}: {
  /** resume an existing draft, else a fresh one is created at step 1 */
  resumeId?: string | null
  narrow?: boolean
  onClose: () => void
  onFinished: (campaign: Campaign, outcome: 'sent' | 'scheduled') => void
}) {
  const toast = useToast()
  const [step, setStep] = React.useState<StepKey>('details')
  const [campaignId, setCampaignId] = React.useState<string | null>(resumeId ?? null)
  const [draft, setDraft] = React.useState<CampaignDraft>(BLANK)
  const [busy, setBusy] = React.useState(false)

  const identities = useAsync(() => listIdentities(), [])
  const identityRows = identities.data ?? []

  // Resuming: load the draft's saved fields and jump to the first gap.
  const resumed = useAsync(
    async () => (resumeId ? await getCampaign(resumeId) : null),
    [resumeId],
  )
  React.useEffect(() => {
    if (!resumed.data) return
    const c = resumed.data
    setDraft({
      name: c.name,
      smtp_account_id: c.smtp_account_id,
      delay_enabled: c.delay_enabled,
      delay_mode: c.delay_mode,
      delay_hours: Number(c.delay_hours),
      delay_minutes: c.delay_minutes,
      random_window_minutes: c.random_window_minutes,
      daily_cap: c.daily_cap,
      throttle_seconds: c.throttle_seconds,
      status: 'draft',
      from_name: c.from_name,
      reply_to: c.reply_to,
      company: c.company,
      sender_title: c.sender_title,
      starts_at: c.starts_at,
      window_enabled: c.window_enabled,
      window_start_min: c.window_start_min,
      window_end_min: c.window_end_min,
      send_tz: c.send_tz,
    })
    // Jump to the first UNCONFIRMED step under the Template-before-Leads
    // order — the server decides: no template -> template; no leads, or leads
    // that can't render the template -> leads; otherwise straight to review.
    void syncConfirmed(c.id)
      .then(async (r) => {
        if (!r.template) return setStep('template')
        if (r.leadCount === 0) return setStep('leads')
        const gapList = await getLeadFieldGaps(c.id, r.template)
        setStep(gapList.length ? 'leads' : 'review')
      })
      .catch(() => setStep('template'))
    // syncConfirmed is a stable callback declared further down — the effect
    // only calls it after mount, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumed.data])

  // --- per-step data ---------------------------------------------------------
  const [leadPage, setLeadPage] = React.useState(0)
  // Same 'leads' memory as the Leads tab, so the choice follows the user.
  const [leadPageSize, setLeadPageSize] = usePageSize('leads', LEADS_PAGE_SIZE)
  const leads = useAsync(
    async () =>
      campaignId
        ? await listLeads({
            campaignId,
            limit: leadPageSize,
            offset: leadPage * leadPageSize,
          })
        : { rows: [] as Lead[], count: 0 },
    [campaignId, step, leadPage, leadPageSize],
  )
  // THE LIBRARY: every saved template is choosable here, not just the ones this
  // campaign happens to have created.
  const templates = useAsync(async () => await listTemplates(), [step])

  const leadCount = leads.data?.count ?? 0

  /**
   * SERVER-CONFIRMED progress. A step is only ticked once a read-back proved
   * its data landed — never because the user clicked past it. `template` holds
   * the campaign's bound template as the server reports it, which is exactly
   * what the send path will resolve.
   */
  const [confirmed, setConfirmed] = React.useState<{
    details: boolean
    leads: boolean
    template: Template | null
  }>({ details: !!resumeId, leads: false, template: null })

  /** Inline, per-step failure text. Cleared on every new attempt. */
  const [stepError, setStepError] = React.useState<string | null>(null)
  React.useEffect(() => setStepError(null), [step])

  const boundTemplate = confirmed.template

  // The reconciliation panel's data: which template tokens the current leads
  // cannot fill, counted by the server. Recomputed whenever the roster changes;
  // the Continue gate re-runs the same check rather than trusting this cache.
  const fieldGaps = useAsync<LeadFieldGap[]>(
    async () =>
      campaignId && step === 'leads'
        ? await getLeadFieldGaps(campaignId, boundTemplate)
        : [],
    [campaignId, step, boundTemplate, leads.data],
  )

  // --- schedule choice (step 4) ---------------------------------------------
  const [mode, setMode] = React.useState<'now' | 'schedule'>('now')
  const [startDate, setStartDate] = React.useState('')
  const [startTime, setStartTime] = React.useState('09:00')
  const [tz, setTz] = React.useState(LOCAL_TZ)
  const [windowOn, setWindowOn] = React.useState(false)

  const finishLabel = mode === 'schedule' ? 'Schedule campaign' : 'Send now'

  // A green check means the server said yes, not that the user got past it.
  const doneSteps: Record<StepKey, boolean> = {
    details: confirmed.details && !!campaignId,
    leads: confirmed.leads,
    template: !!confirmed.template,
    review: false,
  }

  // --- actions ---------------------------------------------------------------

  /**
   * Re-reads the campaign from the server and republishes what is actually
   * true. Every tick in the stepper comes from here.
   */
  const syncConfirmed = React.useCallback(async (id: string) => {
    const readiness = await getCampaignReadiness(id)
    setConfirmed({
      details: !!readiness.campaign && !!readiness.identity,
      leads: readiness.leadCount > 0,
      template: readiness.template,
    })
    return readiness
  }, [])

  // Keep the stepper honest whenever the step or the campaign changes — a tick
  // must never outlive the data that earned it.
  React.useEffect(() => {
    if (!campaignId) return
    void syncConfirmed(campaignId).catch(() => {
      /* a background refresh must not trap the user; gates re-check on click */
    })
  }, [campaignId, step, syncConfirmed])

  /** Step 1 → creates or updates the DRAFT so later steps can attach to it. */
  async function saveDetails(): Promise<boolean> {
    if (!draft.name.trim()) {
      setStepError('Give the campaign a name.')
      return false
    }
    if (draft.reply_to?.trim() && !isValidEmail(draft.reply_to)) {
      setStepError(`Reply-to is not a valid email: ${draft.reply_to}`)
      return false
    }
    // Same rule the control enforces, re-checked here so no path out of step 1
    // can save a delay under the floor.
    const delayProblem = validateDelay(draft)
    if (delayProblem) {
      setStepError(delayProblem)
      return false
    }
    // A campaign with no deliverable sender can never send, so that is not a
    // valid step 1 either: require an explicit identity or a default to inherit.
    if (!identityRows.length) {
      setStepError('Add a sending identity first — a campaign needs a mailbox to send from.')
      return false
    }
    if (!draft.smtp_account_id && !identityRows.some((i) => i.is_default)) {
      setStepError('Pick a sender — there is no default identity to fall back on.')
      return false
    }

    setBusy(true)
    try {
      const saved = campaignId
        ? await updateCampaign(campaignId, draft)
        : await createCampaign({ ...draft, status: 'draft' })

      // CONFIRM the write landed before letting anyone move on.
      const readiness = await syncConfirmed(saved.id)
      if (!readiness.campaign) {
        setStepError('The campaign did not save — it is not on the server. Try again.')
        return false
      }
      if (!readiness.identity) {
        setStepError('Saved, but no sending identity resolved. Pick one before continuing.')
        return false
      }
      setCampaignId(saved.id)
      return true
    } catch (e) {
      setStepError(e instanceof Error ? e.message : String(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  /**
   * Leads gate: the server's own count, then THE RECONCILIATION CHECK — every
   * lead must be able to render the bound template. A lead missing {{role}}
   * would fail at send time with "Unresolved merge fields"; it is refused here
   * instead, while the user is still looking at the leads that need fixing.
   */
  async function confirmLeads(): Promise<boolean> {
    if (!campaignId) return false
    setBusy(true)
    try {
      const readiness = await syncConfirmed(campaignId)
      if (readiness.leadCount === 0) {
        setStepError('Add at least one lead — nothing can send to an empty campaign.')
        return false
      }
      const gapList = await getLeadFieldGaps(campaignId, readiness.template)
      if (gapList.length) {
        setStepError(
          `Can't continue — ${describeGaps(gapList)}. Fill them in, re-import, or remove them.`,
        )
        return false
      }
      return true
    } catch (e) {
      setStepError(e instanceof Error ? e.message : String(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  /**
   * Step 3 gate: the campaign must resolve a template ON THE SERVER.
   *
   * This is the read-back the old wizard never did — it advanced on local
   * state and left campaigns with nothing bound, which is why finished
   * campaigns reported "no template" at send time.
   */
  async function confirmTemplate(): Promise<boolean> {
    if (!campaignId) return false
    setBusy(true)
    try {
      const readiness = await syncConfirmed(campaignId)
      if (!readiness.template) {
        setStepError(
          'Add a template first — it has to be bound to this campaign before anything can send.',
        )
        return false
      }
      return true
    } catch (e) {
      setStepError(e instanceof Error ? e.message : String(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  async function advance() {
    setStepError(null)
    if (step === 'details') {
      if (await saveDetails()) setStep('template')
      return
    }
    if (step === 'template') {
      if (await confirmTemplate()) setStep('leads')
      return
    }
    if (step === 'leads') {
      if (await confirmLeads()) setStep('review')
      return
    }
    await finish()
  }

  /** The only path out of draft. */
  async function finish() {
    if (!campaignId) return
    if (mode === 'schedule' && !startDate) {
      setStepError('Pick a start date.')
      return
    }

    setBusy(true)
    try {
      // RE-VERIFY the whole campaign server-side, immediately before commit.
      // Nothing incomplete may reach a sendable status by any path.
      const readiness = await syncConfirmed(campaignId)
      if (!readiness.ready) {
        refuse(readiness.missing)
        return
      }

      // The reconciliation gate, re-run at the commit: the template (or the
      // leads) may have changed since the Leads step was ticked, and nothing
      // that would fail "Unresolved merge fields" at send time may launch.
      const gapList = await getLeadFieldGaps(campaignId, readiness.template)
      if (gapList.length) {
        setStepError(
          `Can't send yet — ${describeGaps(gapList)}. Fill them in, re-import, or remove them.`,
        )
        setStep('leads')
        return
      }

      // Never launch a campaign whose send-wide constants are blank. These are
      // wrong for EVERY email, not for one recipient, so they are caught here
      // rather than failing a thousand rows one at a time at send time.
      if (readiness.template && readiness.identity) {
        const issues = unresolvedTokens(readiness.template, {
          sender: resolveSenderVars({
            identity: readiness.identity,
            campaign: readiness.campaign,
          }),
        })
        if (issues.length) {
          setStepError(
            `Can't send yet — ${issues
              .map((i) => `{{${i.token}}} is empty (${i.fix})`)
              .join('; ')}.`,
          )
          setStep('details')
          return
        }
      }

      const startsAt =
        mode === 'schedule'
          ? // Interpreted in the chosen timezone by the browser's own parser when
            // the tz matches local; stored as an absolute instant either way.
            new Date(`${startDate}T${startTime || '09:00'}`).toISOString()
          : null

      const campaign = await activateCampaign(campaignId, {
        starts_at: startsAt,
        window_enabled: windowOn,
        window_start_min: 540,
        window_end_min: 1020,
        send_tz: tz,
      })

      // "Send now" has to actually send. Activating only flips the campaign to
      // active — the queue then waits for the next cron tick, which is why
      // finishing the wizard used to land on "Scheduled" and force a trip to the
      // dashboard to press "Process due sends now". Run that same tick here.
      if (mode === 'now') {
        try {
          const tick = await processDueSendsNow()
          if (tick.sent > 0) {
            toast('success', `Sent ${tick.sent} email${tick.sent === 1 ? '' : 's'}.`)
          } else if (tick.would_send > 0) {
            toast(
              'info',
              `Dry run — ${tick.would_send} would have been sent`,
              'Set SEND_ENABLED=true to deliver for real.',
            )
          } else if (tick.failed > 0) {
            toast('error', `${tick.failed} failed to send`, 'See Logs for the reason.')
          } else {
            // Delay is on, or a daily window/cap is holding them back. The cron
            // will take them when they come due; say so instead of implying the
            // click failed.
            toast('info', 'Campaign is live', 'Queued mail sends automatically when due.')
          }
        } catch (e) {
          // The campaign IS live and the cron picks it up within the minute, so
          // a failed manual tick is not a failed launch. Say exactly that.
          toast(
            'info',
            'Campaign is live — sending starts within a minute',
            `Immediate run failed: ${e instanceof Error ? e.message : String(e)}`,
          )
        }
      }

      onFinished(campaign, mode === 'schedule' ? 'scheduled' : 'sent')
    } catch (e) {
      // activateCampaign re-checks too, so this catches anything that changed
      // underneath us between the check and the commit.
      if (e instanceof IncompleteCampaignError) refuse(e.missing)
      else setStepError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Bind a template to this campaign AND confirm it stuck.
   *
   * Step 3 hands every template it creates, imports or picks through here, so
   * there is exactly one way a wizard campaign gets a template — and it is the
   * same link the sender reads. Throws on failure so the caller can report it.
   */
  async function bindTemplate(templateId: string): Promise<void> {
    if (!campaignId) throw new Error('Save the campaign details first')
    await bindCampaignTemplate(campaignId, templateId)
    const readiness = await syncConfirmed(campaignId)
    if (!readiness.template) {
      throw new Error('The template did not bind to this campaign')
    }
    setStepError(null)
  }

  /** Refuse to launch, say what is missing, and go to the step that owns it. */
  function refuse(missing: MissingPiece[]) {
    setStepError(`Can't send yet — ${missing.map((m) => MISSING_LABEL[m]).join(', ')}.`)
    const first = MISSING_STEP[missing[0]]
    if (first) setStep(first)
  }

  /** Abandon: discard an empty draft, keep one with real work. */
  async function close() {
    if (campaignId) {
      try {
        const fate = await discardDraftIfEmpty(campaignId)
        if (fate === 'kept') {
          toast('info', 'Draft saved', 'Resume it from the Campaigns tab')
        }
      } catch {
        /* closing must never trap the user */
      }
    }
    onClose()
  }

  /**
   * The button stays clickable so a refusal can EXPLAIN itself — every gate is
   * an async server check inside advance(), and failures land in stepError
   * rather than being hidden behind a disabled button.
   */
  const working = busy || (step === 'leads' && leads.loading)

  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          // On a phone the 246px rail left ~105px for the form. Stack instead:
          // the stepper becomes a compact strip ABOVE the panel.
          flexDirection: narrow ? 'column' : 'row',
        }}
      >
        {/* ---- stepper: rail on desktop, horizontal strip on small screens ---- */}
        <div
          className={narrow ? 'scroll-x' : undefined}
          style={
            narrow
              ? {
                  flex: 'none',
                  borderBottom: '1px solid var(--border-default)',
                  background: 'var(--surface-box-header)',
                  padding: '8px 10px',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 6,
                }
              : {
                  width: 246,
                  flex: 'none',
                  borderRight: '1px solid var(--border-default)',
                  background: 'var(--surface-box-header)',
                  padding: '14px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  overflowY: 'auto',
                }
          }
        >
          {STEPS.map((s, i) => {
            const active = step === s.key
            const done = doneSteps[s.key] && !active
            const clickable = done || doneSteps[s.key]
            return (
              <button
                key={s.key}
                type="button"
                disabled={!clickable && !active}
                onClick={() => clickable && setStep(s.key)}
                title={narrow ? s.label : undefined}
                style={{
                  display: 'flex',
                  gap: narrow ? 7 : 10,
                  alignItems: narrow ? 'center' : 'flex-start',
                  textAlign: 'left',
                  padding: narrow ? '8px 11px' : '9px 10px',
                  minHeight: narrow ? 40 : undefined,
                  flex: 'none',
                  border: 'none',
                  borderRadius: narrow ? 999 : 6,
                  background: active ? 'var(--surface-card)' : 'transparent',
                  cursor: clickable || active ? 'pointer' : 'default',
                  fontFamily: 'var(--font-sans)',
                  whiteSpace: 'nowrap',
                }}
              >
                <StepDot index={i} active={active} done={done} />
                {/* the hint line is desktop-only — a chip carries just the label */}
                <span style={{ minWidth: 0, display: narrow && !active ? 'none' : 'block' }}>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 13,
                      fontWeight: 600,
                      color: active
                        ? 'var(--accent-text)'
                        : done
                          ? 'var(--text-primary)'
                          : 'var(--text-muted)',
                    }}
                  >
                    {s.label}
                  </span>
                  {!narrow && (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11.5,
                        color: 'var(--text-muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {summaryFor(s.key, { draft, leadCount, boundTemplate, mode, finishLabel }) ||
                        s.hint}
                    </span>
                  )}
                </span>
              </button>
            )
          })}
        </div>

        {/* ---- step body ---- */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowY: 'auto',
            padding: narrow ? 12 : 20,
          }}
        >
          {step === 'details' && (
            <DetailsStep
              narrow={narrow}
              draft={draft}
              setDraft={setDraft}
              identities={(identities.data ?? []).map((i) => ({
                value: i.id,
                label: `${i.label} — ${i.from_email}`,
              }))}
              noIdentities={(identities.data ?? []).length === 0}
            />
          )}

          {step === 'leads' && campaignId && (
            <LeadsStep
              campaign={{ id: campaignId, name: draft.name }}
              rows={leads.data?.rows ?? []}
              total={leadCount}
              page={leadPage}
              pageSize={leadPageSize}
              onPageChange={setLeadPage}
              onPageSizeChange={(n) => {
                setLeadPageSize(n)
                setLeadPage(0)
              }}
              loading={leads.loading}
              error={leads.error}
              expected={requiredLeadFields(boundTemplate).map((f) => f.token)}
              gaps={fieldGaps.data ?? []}
              onChanged={() => leads.reload()}
              onRetry={() => leads.reload()}
            />
          )}

          {step === 'template' && campaignId && (
            <TemplateStep
              campaignId={campaignId}
              campaign={draft}
              identities={identities.data ?? []}
              templates={templates.data ?? []}
              boundTemplateId={confirmed.template?.id ?? null}
              onBind={bindTemplate}
              loading={templates.loading}
              error={templates.error}
              leadRows={leads.data?.rows ?? []}
              narrow={narrow}
              onChanged={() => templates.reload()}
              onRetry={() => templates.reload()}
            />
          )}

          {step === 'review' && campaignId && (
            <ReviewStep
              draft={draft}
              leadCount={leadCount}
              leadPeek={leads.data?.rows?.slice(0, 4) ?? []}
              template={boundTemplate}
              mode={mode}
              setMode={setMode}
              startDate={startDate}
              setStartDate={setStartDate}
              startTime={startTime}
              setStartTime={setStartTime}
              tz={tz}
              setTz={setTz}
              windowOn={windowOn}
              setWindowOn={setWindowOn}
              onEdit={setStep}
            />
          )}
        </div>
      </div>

      {/* ---- persistent footer ---- */}
      <div
        className="wrap-sm"
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: narrow ? '10px 12px' : '10px 20px',
          borderTop: '1px solid var(--border-default)',
          background: 'var(--surface-box-header)',
        }}
      >
        <Button variant="ghost" size="sm" disabled={busy} onClick={close}>
          Cancel
        </Button>

        {/* the refusal, in the same place every time */}
        {stepError && (
          <span
            role="alert"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              minWidth: 0,
              padding: '3px 9px',
              borderRadius: 6,
              background: 'var(--danger-soft, var(--status-failed-bg))',
              color: 'var(--danger-text)',
              fontSize: 12.5,
              fontWeight: 500,
            }}
          >
            <Ic.warning size={13} />
            <span
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={stepError}
            >
              {stepError}
            </span>
          </span>
        )}

        <div style={{ flex: 1 }} />
        {step !== 'details' && (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => {
              const i = STEPS.findIndex((s) => s.key === step)
              setStep(STEPS[Math.max(0, i - 1)].key)
            }}
          >
            Back
          </Button>
        )}
        <Button size="sm" disabled={working} onClick={advance}>
          {busy
            ? step === 'review'
              ? 'Checking…'
              : 'Saving…'
            : step === 'review'
              ? finishLabel
              : step === 'details'
                ? 'Save & continue'
                : 'Continue'}
        </Button>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------

function summaryFor(
  key: StepKey,
  ctx: {
    draft: CampaignDraft
    leadCount: number
    boundTemplate: Template | null
    mode: 'now' | 'schedule'
    finishLabel: string
  },
): string {
  if (key === 'details') return ctx.draft.name.trim()
  if (key === 'leads') return ctx.leadCount ? `${ctx.leadCount} lead${ctx.leadCount === 1 ? '' : 's'}` : ''
  if (key === 'template') return ctx.boundTemplate?.name?.trim() || ctx.boundTemplate?.subject || ''
  return ctx.finishLabel
}

function StepDot({ index, active, done }: { index: number; active: boolean; done: boolean }) {
  return (
    <span
      style={{
        width: 20,
        height: 20,
        flex: 'none',
        marginTop: 1,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
        background: done
          ? 'var(--status-approved-bg)'
          : active
            ? 'var(--accent)'
            : 'var(--surface-sunken)',
        color: done
          ? 'var(--status-approved-fg)'
          : active
            ? 'var(--text-on-accent)'
            : 'var(--text-faint)',
      }}
    >
      {done ? <Ic.check size={12} /> : index + 1}
    </span>
  )
}

function SectionTitle({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
      {hint && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{hint}</div>}
    </div>
  )
}

// --- step 1 -------------------------------------------------------------------

function DetailsStep({
  narrow,
  draft,
  setDraft,
  identities,
  noIdentities,
}: {
  narrow?: boolean
  draft: CampaignDraft
  setDraft: React.Dispatch<React.SetStateAction<CampaignDraft>>
  identities: { value: string; label: string }[]
  noIdentities: boolean
}) {
  const set = <K extends keyof CampaignDraft>(k: K, v: CampaignDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }))

  return (
    <div style={{ maxWidth: narrow ? '100%' : 620, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionTitle hint="Nothing sends until you finish the wizard.">Campaign details</SectionTitle>

      <Field label="Name">
        <Input value={draft.name} onChange={(v) => set('name', v)} placeholder="Q3 launch" autoFocus />
      </Field>

      <Field
        label="Send from"
        hint={
          noIdentities
            ? 'No senders yet — add one on the Identities tab first.'
            : 'The mailbox that authenticates and delivers'
        }
      >
        <Select
          options={[{ value: '', label: 'Use the default identity' }, ...identities]}
          value={draft.smtp_account_id ?? ''}
          onChange={(v) => set('smtp_account_id', v || null)}
        />
      </Field>

      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="From name (optional)" hint="Overrides the sender's display name">
          <Input
            value={draft.from_name ?? ''}
            onChange={(v) => set('from_name', v || null)}
            placeholder="StuHelp"
          />
        </Field>
        <Field label="Reply-to" hint="Sent from your mailbox; replies go here instead">
          <Input
            type="email"
            value={draft.reply_to ?? ''}
            onChange={(v) => set('reply_to', v || null)}
            placeholder="hiring@company.com"
          />
        </Field>
      </div>

      {/* The send-wide constants. These fill {{company}} / {{sender_title}} for
          EVERY email in the campaign — they are not CSV columns, and a template
          that uses them cannot launch until they resolve. */}
      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field
          label="Company"
          hint="The hiring organisation — fills {{company}} in every email"
        >
          <Input
            value={draft.company ?? ''}
            onChange={(v) => set('company', v || null)}
            placeholder="Millenium Eye Center"
          />
        </Field>
        <Field label="Sender title (optional)" hint="Fills {{sender_title}}, e.g. Talent Team">
          <Input
            value={draft.sender_title ?? ''}
            onChange={(v) => set('sender_title', v || null)}
            placeholder="Talent Team"
          />
        </Field>
      </div>

      <DelayControl
        value={{
          delay_enabled: draft.delay_enabled,
          delay_mode: draft.delay_mode,
          delay_minutes: draft.delay_minutes,
          delay_hours: draft.delay_hours,
          random_window_minutes: draft.random_window_minutes,
        }}
        onChange={(next) => setDraft((d) => ({ ...d, ...next }))}
      />

      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Daily cap" hint="Max sends per UTC day">
          <Input
            type="number"
            min={1}
            value={String(draft.daily_cap)}
            onChange={(v) => set('daily_cap', Number(v) || 1)}
          />
        </Field>
        <Field label="Throttle" hint="Seconds between sends">
          <Input
            type="number"
            min={0}
            value={String(draft.throttle_seconds)}
            onChange={(v) => set('throttle_seconds', Number(v) || 0)}
          />
        </Field>
      </div>
    </div>
  )
}
